import { colorPalette } from '../constants';
import { MIN_CASES_FOR_TREND, TREND_STATUS } from '../constants/quality-metric.constants';

import _ from 'lodash';

export const OPERATORS = {
  GT: '>',
  LT: '<',
};

export const isSuccess = ({ total, success, groupGoal }) => {
  if (total === 0) {
    return false;
  }

  return success / total >= groupGoal;
};

export const displayPercentage = ({ total, success, invert = false }) => {
  if (total === 0) return '0%';
  const percent = Math.round((success / total) * 100);
  return invert ? `${100 - percent}%` : `${percent}%`;
};

export const displayGoal = ({ groupGoal, invert = false }) => {
  if (groupGoal === 0) return '0%';
  const percent = `${calculateGoalPercentage({ groupGoal, invert, digits: 1 })}%`;
  const operator = invert ? OPERATORS.LT : OPERATORS.GT;
  return `${operator} ${percent}`;
};

export const calculateGoalPercentage = ({ groupGoal, invert = false, digits = 0 }) => {
  const percent = invert ? 1 - groupGoal : groupGoal;
  const value = percent * 100;
  if (digits > 0) {
    const fixed = value.toFixed(digits);
    // If the value is an integer (e.g., 80.0), show as integer
    if (fixed.endsWith('.0')) {
      return String(Math.round(value));
    }
    return fixed;
  }
  return Math.round(value);
};

export const TREND_POINT_STATUS = {
  SUCCESS: 'success',
  FAILURE: 'failure',
  NO_DATA: 'no_data',
};

export const trendDataPointStatus = ({ rate, hasData, goal, invert = false }) => {
  if (!hasData) return TREND_POINT_STATUS.NO_DATA;
  const isSuccessDataPoint = invert ? rate <= goal : rate >= goal;
  return isSuccessDataPoint ? TREND_POINT_STATUS.SUCCESS : TREND_POINT_STATUS.FAILURE;
};

const TREND_POINT_COLORS = {
  [TREND_POINT_STATUS.SUCCESS]: colorPalette.greenPositive,
  [TREND_POINT_STATUS.FAILURE]: colorPalette.redNegative,
  [TREND_POINT_STATUS.NO_DATA]: colorPalette.neutral4,
};

export const trendDataPointColor = params => TREND_POINT_COLORS[trendDataPointStatus(params)];

/**
 * Calculate user and department cases based on the quality metric and user metric.
 * @export
 * @param {{
 *  qualityMetric: { totalGroupRecords: number, totalGroupSuccess: number, invert?: boolean },
 *  userMetric: { totalRecords: number, totalSuccess: number, invert?: boolean }
 * }} { qualityMetric, userMetric }
 * @return {{
 *  userCases: number,
 *  departmentCases: number
 * }}
 */
export function calculateCases({ qualityMetric, userMetric }) {
  let userCases = userMetric.totalSuccess;
  let departmentCases = qualityMetric.totalGroupSuccess;

  if (userMetric.invert) {
    userCases = userMetric.totalRecords - userMetric.totalSuccess;
    departmentCases = qualityMetric.totalGroupRecords - qualityMetric.totalGroupSuccess;
  }

  return {
    userCases,
    departmentCases,
  };
}

/**
 * Get the Y-axis range for the chart based on trends and goal.
 *
 * @export
 * @param {{
 *  trends: Array<{ rate: number, hasData: boolean }>,
 *  goal: number
 * }} {trends, goal}
 * @return {*}
 */
export function yAxisRange({ trends, goal }) {
  const datapoints = _.map(trends, 'rate').concat(goal);
  const minTrendValue = _.min(datapoints);
  const maxTrendValue = _.max(datapoints);

  const yAxisMaxLine = _.min([maxTrendValue + 10, 100]); // ensure the maximum is at least 100 for percentage display
  const yAxisMinLine = _.max([minTrendValue - 10, 0]); // ensure the minimum is at least 0 for percentage display

  // Make sure the Y-axis is in steps of 5
  const step = 5;
  const yAxisMin = Math.floor(yAxisMinLine / step) * step;
  const yAxisMax = Math.ceil(yAxisMaxLine / step) * step;

  return { yAxisMin, yAxisMax };
}

export const adjustTrend = invert => item => ({
  ...item,
  rate: invert ? 100 - item.rate : item.rate,
});

/**
 * Calculate the trend delta between the two most recent periods with data.
 * @param {{
 *  trends: Array<{ year: number, month: number, rate: number | null, hasData: boolean }>,
 *  invert: boolean
 * }} params
 * @return {{
 *  status: string,
 *  delta: number,
 *  absoluteDelta: number,
 *  comparisonMonth: number,
 *  comparisonYear: number
 * }}
 */
export function calculateTrendDelta({ trends, invert = false }) {
  const withData = _.sortBy(
    _.filter(
      trends,
      item => item.hasData && item.rate !== null && item.total >= MIN_CASES_FOR_TREND,
    ),
    ['year', 'month'],
  );

  // Need at least 2 data points to calculate a trend delta
  if (withData.length < 2) {
    return { status: TREND_STATUS.NA };
  }

  const prior = withData[withData.length - 2];
  const current = withData[withData.length - 1];

  const priorRate = invert ? 100 - prior.rate : prior.rate;
  const currentRate = invert ? 100 - current.rate : current.rate;

  const delta = _.round(currentRate - priorRate, 1);

  let status = TREND_STATUS.NO_CHANGE;
  if (delta > 0) status = TREND_STATUS.UP;
  if (delta < 0) status = TREND_STATUS.DOWN;

  return {
    status,
    delta,
    absoluteDelta: Math.abs(delta),
    comparisonMonth: prior.month,
    comparisonYear: prior.year,
    currentMonth: current.month,
    currentYear: current.year,
  };
}

/**
 * Sort metrics with their trend data by compliance status and trend direction.
 * Priority: (1) non-compliant, (2) compliant but trending down, (3) others.
 * Within each group: sort by absolute trend magnitude desc, then alphabetically.
 * @param {Array<{ metric: object, trend: object, isCompliant: boolean }>} items
 * @return {Array}
 */
export function sortMetricsWithTrends(items) {
  const grouped = _.groupBy(items, item => {
    if (!item.isCompliant) return 'nonCompliant';
    if (item.trend?.status === TREND_STATUS.DOWN) return 'compliantDown';
    return 'other';
  });

  const sortGroup = group =>
    _.orderBy(
      group,
      [item => item.trend?.absoluteDelta || 0, item => item.metric.title],
      ['desc', 'asc'],
    );

  return [
    ...sortGroup(grouped.nonCompliant || []),
    ...sortGroup(grouped.compliantDown || []),
    ...sortGroup(grouped.other || []),
  ];
}

/**
 * Format a trend period as mm/yy (e.g. "03/26").
 * @param {number} month
 * @param {number} year
 * @return {string}
 */
export function formatTrendPeriod(month, year) {
  if (!month || !year) return '';
  const mm = String(month).padStart(2, '0');
  const yy = String(year).slice(-2);
  return `${mm}/${yy}`;
}

/**
 * Derive trend maps, sorted metrics, and comparison label from a metrics list
 * that already carries embedded trend arrays (post-backend-migration shape:
 * each item from GET /v1/quality-metric/user is a userMetric with a flat
 * `trends: TrendBucket[]` field).
 *
 * @param {Array<{ metricId: string, invert?: boolean, trends?: Array, totalRecords: number, totalSuccess: number, groupGoal: number }>} metrics
 * @return {{ userTrendsMap: Object, rawUserTrendsMap: Object, sortedMetrics: Array, comparisonLabel: string }}
 */
export function deriveMetricsTrends(metrics = []) {
  const userTrendsMap = {};
  const rawUserTrendsMap = {};

  for (const metric of metrics) {
    const raw = metric.trends || [];
    rawUserTrendsMap[metric.metricId] = raw;
    userTrendsMap[metric.metricId] = calculateTrendDelta({ trends: raw, invert: metric.invert });
  }

  const sortedMetrics = sortMetricsWithTrends(
    metrics.map(metric => ({
      metric,
      trend: userTrendsMap[metric.metricId],
      isCompliant: isSuccess({
        total: metric.totalRecords,
        success: metric.totalSuccess,
        groupGoal: metric.groupGoal,
      }),
    })),
  ).map(item => item.metric);

  const firstWithComparison = Object.values(userTrendsMap).find(
    t => t?.comparisonMonth && t?.comparisonYear,
  );
  const comparisonLabel = firstWithComparison
    ? formatTrendPeriod(firstWithComparison.comparisonMonth, firstWithComparison.comparisonYear)
    : '';

  return { userTrendsMap, rawUserTrendsMap, sortedMetrics, comparisonLabel };
}
